// .../builder/canvas/richSchema.js
// Schema do texto do e-mail: só o que o servidor aceita (p, br, b, i, u, a,
// listas, span com cor/tamanho). Colar do Word/Docs passa por aqui e o
// que não está no schema some (classes, fontes, mso-*).
import {
  Schema,
  DOMParser as PMDOMParser,
  DOMSerializer,
} from 'prosemirror-model';
import { addListNodes } from 'prosemirror-schema-list';

export const SAFE_URL = /^(https?:\/\/|mailto:|tel:|\/|\{\{)/i;
export const VARIABLES = [
  'primeiro_nome',
  'nome',
  'empresa',
  'vendedor',
  'vendedor_email',
  'remetente',
  'endereco',
];

const toHex = color => {
  const m = String(color).match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!m) return /^#[0-9a-f]{6}$/i.test(color) ? color.toLowerCase() : null;
  return `#${[m[1], m[2], m[3]].map(n => Number(n).toString(16).padStart(2, '0')).join('')}`;
};

const marks = {
  link: {
    attrs: { href: {} },
    inclusive: false,
    parseDOM: [
      {
        tag: 'a[href]',
        getAttrs: dom =>
          SAFE_URL.test(dom.getAttribute('href') || '')
            ? { href: dom.getAttribute('href') }
            : false,
      },
    ],
    toDOM: mark => ['a', { href: mark.attrs.href, target: '_blank' }, 0],
  },
  strong: {
    parseDOM: [
      { tag: 'strong' },
      {
        tag: 'b',
        getAttrs: dom => (dom.style.fontWeight === 'normal' ? false : null),
      },
      {
        style: 'font-weight',
        getAttrs: v => (/^(bold|[6-9]00)$/.test(v) ? null : false),
      },
    ],
    toDOM: () => ['b', 0],
  },
  em: {
    parseDOM: [{ tag: 'i' }, { tag: 'em' }, { style: 'font-style=italic' }],
    toDOM: () => ['i', 0],
  },
  underline: {
    parseDOM: [
      { tag: 'u' },
      {
        style: 'text-decoration',
        getAttrs: v => (v.includes('underline') ? null : false),
      },
    ],
    toDOM: () => ['u', 0],
  },
  textStyle: {
    attrs: { color: { default: null }, size: { default: null } },
    parseDOM: [
      {
        tag: 'span[style]',
        getAttrs: dom => {
          const color = dom.style.color ? toHex(dom.style.color) : null;
          const size = /^\d+px$/.test(dom.style.fontSize)
            ? dom.style.fontSize
            : null;
          const isPlainBlack = color === '#000000' && !size;
          return (color || size) && !isPlainBlack ? { color, size } : false;
        },
      },
    ],
    toDOM: mark => [
      'span',
      {
        style: [
          mark.attrs.color && `color:${mark.attrs.color}`,
          mark.attrs.size && `font-size:${mark.attrs.size}`,
        ]
          .filter(Boolean)
          .join(';'),
      },
      0,
    ],
  },
};

const blockNodes = {
  doc: { content: 'block+' },
  paragraph: {
    content: 'inline*',
    group: 'block',
    parseDOM: [
      { tag: 'p' },
      { tag: 'div' },
      { tag: 'h1' },
      { tag: 'h2' },
      { tag: 'h3' },
      { tag: 'h4' },
    ],
    toDOM: () => ['p', 0],
  },
  text: { group: 'inline' },
  hard_break: {
    inline: true,
    group: 'inline',
    selectable: false,
    parseDOM: [{ tag: 'br' }],
    toDOM: () => ['br'],
  },
};

const inlineNodes = {
  doc: { content: 'inline*' },
  text: { group: 'inline' },
  hard_break: {
    inline: true,
    group: 'inline',
    selectable: false,
    parseDOM: [{ tag: 'br' }],
    toDOM: () => ['br'],
  },
};

export const buildSchema = ({ inline = false } = {}) => {
  if (inline) return new Schema({ nodes: inlineNodes, marks });
  const base = new Schema({ nodes: blockNodes, marks });
  return new Schema({
    nodes: addListNodes(base.spec.nodes, 'paragraph block*', 'block'),
    marks,
  });
};

export const parseHtml = (schema, html) => {
  const el = document.createElement('div');
  el.innerHTML = html || '';
  return PMDOMParser.fromSchema(schema).parse(el);
};

export const serializeHtml = (schema, doc) => {
  const el = document.createElement('div');
  el.appendChild(
    DOMSerializer.fromSchema(schema).serializeFragment(doc.content)
  );
  // O navegador normaliza o style (rgb(), espaços, ";" final); reescreve compacto em hex.
  el.querySelectorAll('span[style]').forEach(span => {
    const { color, fontSize } = span.style;
    const css = [
      color && `color:${toHex(color)}`,
      fontSize && `font-size:${fontSize}`,
    ]
      .filter(Boolean)
      .join(';');
    span.setAttribute('style', css);
  });
  return el.innerHTML;
};
